import { DobInput } from '@dedalusdiit/dob-components';
import { useRef, useState } from 'react';
import { useDebounceCallback } from 'usehooks-ts';

const DEFAULT_COMMIT_DELAY_MS = 250;

export interface DebouncedFilterInputProps {
    label: string;
    value: string;
    disabled?: boolean;
    /**
     * Called with the committed value after the debounce elapses (or immediately on blur/Enter).
     * Must be a stable/memoised reference (e.g. via `useCallback`): the debounce is keyed on this
     * callback, so a new function every render would recreate it and drop any pending flush.
     */
    onValueChange: (value: string) => void;
    onEnter?: () => void;
    delay?: number;
}

// A free-text filter input that only commits to `onValueChange` a short while after the user stops typing,
// instead of on every keystroke. Any pending edit is committed immediately on blur or Enter
const DebouncedFilterInput = (props: DebouncedFilterInputProps): React.ReactElement => {
    const { label, value, disabled, onValueChange, onEnter, delay = DEFAULT_COMMIT_DELAY_MS } = props;

    const [localValue, setLocalValue] = useState(value);
    // The `value` prop as of the last render, so an external change of the value can be told apart from a
    // render caused by something else entirely. Only ever compared to its own previous render, never mutated
    // by typing.
    const lastSyncedValueRef = useRef(value);

    const debouncedCommit = useDebounceCallback(onValueChange, delay);

    if (value !== lastSyncedValueRef.current) {
        lastSyncedValueRef.current = value;
        setLocalValue(value);
        debouncedCommit.cancel();
    }

    const handleValueChange = (newValue: string): void => {
        setLocalValue(newValue);
        debouncedCommit(newValue);
    };

    const handleBlur = (): void => {
        debouncedCommit.flush();
    };

    const handleEnter = (): void => {
        debouncedCommit.flush();
        onEnter?.();
    };

    return (
        <DobInput
            type="filter"
            label={label}
            disabled={disabled}
            value={localValue}
            onValueChange={handleValueChange}
            onBlur={handleBlur}
            onEnter={handleEnter}
        />
    );
};

export default DebouncedFilterInput;
